import { useState } from 'react';
import { Scale, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { FormField, Select, Textarea } from '../../../components/ui/Form';
import { useFiscalRegime } from '../../../hooks/useFiscalRegime';
import {
  FISCAL_REGIME_LABELS,
  FISCAL_REGIME_DESCRIPTIONS,
  type FiscalRegime,
  type FiscalRegimeTransitionPreview,
} from '../../../types/fiscal-regime';

const REGIME_BADGE: Record<FiscalRegime, { bg: string; text: string }> = {
  SMT: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
  NORMAL: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
  FORFAITAIRE: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
};

/**
 * Configuration du régime fiscal de l'entreprise (RDC).
 *
 * Affiche le régime courant et permet à un admin d'évoluer vers un autre
 * régime via un wizard de confirmation qui appelle d'abord un preview pour
 * détecter les blocages (TVA déjà comptabilisée par exemple).
 */
export function FiscalRegimeSettings() {
  const { currentRegime, loading, saving, change, preview } = useFiscalRegime();
  const [showModal, setShowModal] = useState(false);

  if (loading || !currentRegime) {
    return (
      <Card title="Régime fiscal" icon={Scale}>
        <p className="text-sm text-muted-foreground py-4 text-center">
          Chargement du régime fiscal…
        </p>
      </Card>
    );
  }

  const badge = REGIME_BADGE[currentRegime];

  return (
    <>
      <Card title="Régime fiscal (RDC)" icon={Scale}>
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${badge.bg} ${badge.text}`}
                >
                  {FISCAL_REGIME_LABELS[currentRegime]}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {FISCAL_REGIME_DESCRIPTIONS[currentRegime]}
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 mt-2">
                <li>
                  • <strong>TVA</strong> :{' '}
                  {currentRegime === 'NORMAL'
                    ? 'assujettie au taux normal 16% (déclaration mensuelle)'
                    : 'exonérée'}
                </li>
                <li>
                  • <strong>IBP</strong> :{' '}
                  {currentRegime === 'SMT' ? "1% du chiffre d'affaires" : '30% du bénéfice fiscal'}
                </li>
                <li>
                  • <strong>Plan comptable recommandé</strong> :{' '}
                  {currentRegime === 'SMT'
                    ? 'SYSCOHADA Minimal'
                    : 'SYSCOHADA Normal'}
                </li>
              </ul>
            </div>
            <Button
              variant="secondary"
              size="sm"
              icon={ArrowRight}
              onClick={() => setShowModal(true)}
            >
              Évoluer le régime
            </Button>
          </div>
        </div>
      </Card>

      {showModal && (
        <ChangeFiscalRegimeModal
          currentRegime={currentRegime}
          saving={saving}
          onClose={() => setShowModal(false)}
          onPreview={preview}
          onConfirm={async (newRegime, reason) => {
            const ok = await change(newRegime, reason);
            if (ok) setShowModal(false);
          }}
        />
      )}
    </>
  );
}

interface ChangeFiscalRegimeModalProps {
  currentRegime: FiscalRegime;
  saving: boolean;
  onClose: () => void;
  onPreview: (newRegime: FiscalRegime) => Promise<FiscalRegimeTransitionPreview | null>;
  onConfirm: (newRegime: FiscalRegime, reason?: string) => Promise<void>;
}

function ChangeFiscalRegimeModal({
  currentRegime,
  saving,
  onClose,
  onPreview,
  onConfirm,
}: ChangeFiscalRegimeModalProps) {
  const [targetRegime, setTargetRegime] = useState<FiscalRegime>(
    currentRegime === 'NORMAL' ? 'SMT' : 'NORMAL',
  );
  const [reason, setReason] = useState('');
  const [previewResult, setPreviewResult] =
    useState<FiscalRegimeTransitionPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const handleLoadPreview = async () => {
    setLoadingPreview(true);
    const result = await onPreview(targetRegime);
    setPreviewResult(result);
    setLoadingPreview(false);
  };

  const handleConfirm = async () => {
    await onConfirm(targetRegime, reason.trim() || undefined);
  };

  const regimeOptions = (['SMT', 'NORMAL', 'FORFAITAIRE'] as FiscalRegime[])
    .filter((r) => r !== currentRegime)
    .map((r) => ({ value: r, label: FISCAL_REGIME_LABELS[r] }));

  return (
    <Modal isOpen onClose={onClose} title="Évolution du régime fiscal" size="lg">
      <div className="space-y-5">
        <div className="flex items-center gap-3 text-sm">
          <span className="px-2 py-1 rounded bg-muted">{FISCAL_REGIME_LABELS[currentRegime]}</span>
          <ArrowRight className="h-4 w-4" />
          <Select
            value={targetRegime}
            onChange={(e) => {
              setTargetRegime(e.target.value as FiscalRegime);
              setPreviewResult(null);
            }}
            options={regimeOptions}
          />
        </div>

        <div className="text-sm text-muted-foreground p-3 rounded bg-muted/30">
          {FISCAL_REGIME_DESCRIPTIONS[targetRegime]}
        </div>

        <FormField
          label="Motif (audit trail)"
          hint="Optionnel — ex: croissance du CA au-delà du seuil SMT"
        >
          <Textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {!previewResult ? (
          <Button
            variant="secondary"
            onClick={handleLoadPreview}
            isLoading={loadingPreview}
            className="w-full"
          >
            Vérifier les conséquences
          </Button>
        ) : (
          <div className="space-y-3">
            {previewResult.allowed ? (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div className="text-emerald-700 dark:text-emerald-400">
                  Transition autorisée.
                  {!previewResult.isFrameworkAligned && (
                    <div className="mt-1 text-xs">
                      ⚠️ Votre plan comptable actuel n'est pas optimal pour ce régime —
                      pensez à ré-initialiser le plan en{' '}
                      <strong>{previewResult.recommendedFramework}</strong> après
                      l'évolution.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-red-700 dark:text-red-400">
                  <strong>Transition refusée</strong>
                  <div className="mt-1">{previewResult.blockingReason}</div>
                  {previewResult.hasVatActivity && (
                    <div className="mt-1 text-xs">
                      Clôturez d'abord l'exercice courant pour pouvoir évoluer.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            isLoading={saving}
            disabled={!previewResult || !previewResult.allowed}
          >
            Confirmer l'évolution
          </Button>
        </div>
      </div>
    </Modal>
  );
}
